// WP-07 — single source of truth for the screen inventory O-02…O-15 (LLD-05
// Appendix A; UX v1.4 §5). Routes, role grants and nav are all derived from
// this table so the RBAC matrix lives in exactly one place.
//
// Role grants per UX v1.4 §2 (read-only nuances are enforced inside screens
// when the feature verticals land; route access is the shell's concern):
//   - bookkeeper: jobs/clients/worklist/link-back + dashboard view;
//     NO job capture (O-04 — row 72 (ii) re-ruled 2026-10-19 (b), UX-4a),
//     NO calendar/scheduling/booking (UXQ-10), NO quotes (UXQ-9/10), NO
//     foreman history, NO catalogue, NO org settings.
//   - O-15 Org Settings: OWNER ONLY (MT-F13; LLD-05 §2.6/§11).
//   - foreman: no route at all (BR-021 — excluded before routing).
import type { CatalogueKey } from "../i18n/en.catalogue";
import { JOB_CAPTURE_ROLES, type Role } from "../auth/roles";

export const ALL_OFFICE: readonly Role[] = [
  "OWNER",
  "OFFICE_MANAGER",
  "BOOKKEEPER",
];
export const OWNER_AND_OM: readonly Role[] = ["OWNER", "OFFICE_MANAGER"];
export const OWNER_ONLY: readonly Role[] = ["OWNER"];

export interface ScreenDef {
  /** UX screen id (O-02…O-15). */
  id: string;
  /** Router path (relative to the authenticated shell root). */
  path: string;
  titleKey: CatalogueKey;
  roles: readonly Role[];
  /** i18n key for the sidebar entry; screens without one are not in the nav. */
  navKey?: CatalogueKey;
}

export const SCREENS: readonly ScreenDef[] = [
  {
    id: "O-02",
    path: "dashboard",
    titleKey: "screens.o02.title",
    roles: ALL_OFFICE,
    navKey: "nav.dashboard",
  },
  {
    id: "O-03",
    path: "jobs",
    titleKey: "screens.o03.title",
    roles: ALL_OFFICE,
    navKey: "nav.jobs",
  },
  {
    id: "O-04",
    path: "jobs/new",
    titleKey: "screens.o04.title",
    // UX-4a — row 72 (ii) re-ruled 2026-10-19 (b): capture is OWNER /
    // OFFICE_MANAGER (the POST /jobs grant), one source with canCaptureJobs.
    roles: JOB_CAPTURE_ROLES,
  },
  {
    id: "O-05",
    path: "jobs/:jobId",
    titleKey: "screens.o05.title",
    roles: ALL_OFFICE,
  },
  {
    id: "O-06",
    path: "calendar",
    titleKey: "screens.o06.title",
    roles: OWNER_AND_OM,
    navKey: "nav.calendar",
  },
  {
    id: "O-07",
    path: "schedule",
    titleKey: "screens.o07.title",
    roles: OWNER_AND_OM,
  },
  {
    id: "O-08",
    path: "schedule/confirmation",
    titleKey: "screens.o08.title",
    roles: OWNER_AND_OM,
  },
  {
    id: "O-09",
    path: "invoicing",
    titleKey: "screens.o09.title",
    roles: ALL_OFFICE,
    navKey: "nav.invoicing",
  },
  {
    id: "O-10",
    path: "invoicing/link-back",
    titleKey: "screens.o10.title",
    roles: ALL_OFFICE,
  },
  {
    id: "O-11",
    path: "quotes",
    titleKey: "screens.o11.title",
    roles: OWNER_AND_OM,
    navKey: "nav.quotes",
  },
  {
    id: "O-12",
    path: "clients",
    titleKey: "screens.o12.title",
    roles: ALL_OFFICE,
    navKey: "nav.clients",
  },
  {
    id: "O-13",
    path: "foremen",
    titleKey: "screens.o13.title",
    roles: OWNER_AND_OM,
    navKey: "nav.foremen",
  },
  {
    id: "O-14",
    path: "catalogue",
    titleKey: "screens.o14.title",
    roles: OWNER_AND_OM,
    navKey: "nav.catalogue",
  },
  {
    id: "O-15",
    path: "settings/organization",
    titleKey: "screens.o15.title",
    roles: OWNER_ONLY,
    navKey: "nav.settings",
  },
];

export interface NavItem {
  id: string;
  path: string;
  labelKey: CatalogueKey;
}

/** Role-filtered sidebar (LLD-05 §4 AppShell.navFor). Hidden, not greyed. */
export function navFor(role: Role): NavItem[] {
  return SCREENS.filter(
    (screen) => screen.navKey && screen.roles.includes(role),
  ).map((screen) => ({
    id: screen.id,
    path: `/${screen.path}`,
    // navKey is guaranteed by the filter above.
    labelKey: screen.navKey as CatalogueKey,
  }));
}
